import * as z from 'zod/v4';

export const storefrontAddonTypeValues = [
  'announcement_bar',
  'countdown_bar',
  'promo_info_card',
  'recent_purchase_popup',
  'coupon_popup_modal',
  'live_chat',
] as const;

export const storefrontAddonSlotValues = [
  'layout.header.before',
  'layout.header.after',
  'layout.overlay',
  'home.hero.after',
  'home.grid.before',
  'product.buybox.after',
  'layout.footer.before',
  'floating.bottom_right',
] as const;

export const storefrontAddonComponentValues = [
  'announcement_bar',
  'countdown_bar',
  'promo_info_card',
  'recent_purchase_popup',
  'coupon_popup_modal',
  'live_chat',
] as const;

export const StorefrontAddonTypeSchema = z.enum(storefrontAddonTypeValues);
export const StorefrontAddonSlotSchema = z.enum(storefrontAddonSlotValues);
export const StorefrontAddonComponentSchema = z.enum(storefrontAddonComponentValues);

export type StorefrontAddonType = z.infer<typeof StorefrontAddonTypeSchema>;
export type StorefrontAddonSlot = z.infer<typeof StorefrontAddonSlotSchema>;
export type StorefrontAddonComponent = z.infer<typeof StorefrontAddonComponentSchema>;

const HexColorSchema = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Use a valid hex color');

export const announcementBarDisplayModeValues = [
  'static',
  'marquee',
] as const;

export const announcementBarThemePresetValues = [
  'brand_blue',
  'emerald',
  'sunset',
  'rose',
  'charcoal',
  'custom',
] as const;

export const DEFAULT_ANNOUNCEMENT_BAR_ANIMATION_SPEED_SECONDS = 30;

export const AnnouncementBarDisplayModeSchema = z.enum(announcementBarDisplayModeValues);
export const AnnouncementBarThemePresetSchema = z.enum(announcementBarThemePresetValues);

export const countdownBarExpiryBehaviorValues = [
  'hide',
  'message',
] as const;

export const countdownBarDensityValues = [
  'compact',
  'comfortable',
] as const;

export const countdownBarCtaStyleValues = [
  'subtle',
  'outline',
  'solid',
] as const;

export const countdownBarTimerStyleValues = [
  'minimal',
  'boxed',
] as const;

export const promoInfoCardThemePresetValues = [
  'indigo',
  'emerald',
  'amber',
  'rose',
  'slate',
  'custom',
] as const;

export const promoInfoCardLayoutStyleValues = [
  'compact',
  'feature',
  'alert',
] as const;

export const promoInfoCardDensityValues = [
  'compact',
  'comfortable',
] as const;

export const promoInfoCardCtaStyleValues = [
  'subtle',
  'outline',
  'solid',
] as const;

export const promoInfoCardIconVisibilityValues = [
  'show',
  'hide',
] as const;

export const promoInfoCardIconValues = [
  'sparkles',
  'megaphone',
  'gift',
  'truck',
  'shield',
  'support',
  'none',
] as const;

export const CountdownBarExpiryBehaviorSchema = z.enum(countdownBarExpiryBehaviorValues);
export const CountdownBarDensitySchema = z.enum(countdownBarDensityValues);
export const CountdownBarCtaStyleSchema = z.enum(countdownBarCtaStyleValues);
export const CountdownBarTimerStyleSchema = z.enum(countdownBarTimerStyleValues);
export const PromoInfoCardThemePresetSchema = z.enum(promoInfoCardThemePresetValues);
export const PromoInfoCardLayoutStyleSchema = z.enum(promoInfoCardLayoutStyleValues);
export const PromoInfoCardDensitySchema = z.enum(promoInfoCardDensityValues);
export const PromoInfoCardCtaStyleSchema = z.enum(promoInfoCardCtaStyleValues);
export const PromoInfoCardIconSchema = z.enum(promoInfoCardIconValues);
export const PromoInfoCardIconVisibilitySchema = z.enum(promoInfoCardIconVisibilityValues);

export const couponPopupModalThemePresetValues = [
  'midnight',
  'ocean',
  'ember',
  'forest',
  'custom',
] as const;

export const couponPopupModalTriggerValues = [
  'delay',
  'exit_intent',
] as const;

export const CouponPopupModalThemePresetSchema = z.enum(couponPopupModalThemePresetValues);
export const CouponPopupModalTriggerSchema = z.enum(couponPopupModalTriggerValues);

const RelativeOrAbsoluteUrlSchema = z.string().trim().refine((value) => {
  if (value.startsWith('/')) {
    return true;
  }

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}, 'Use a valid absolute URL or a path starting with "/"');

const DatetimeStringSchema = z.string().trim().refine((value) => {
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime());
}, 'Use a valid date and time');

export const AnnouncementBarConfigSchema = z.object({
  text: z.string().trim().min(1, 'Text is required').max(160, 'Text must be 160 characters or fewer'),
  linkLabel: z.string().trim().max(32, 'Link label must be 32 characters or fewer').optional().nullable(),
  linkUrl: RelativeOrAbsoluteUrlSchema.optional().nullable(),
  dismissible: z.boolean().default(false),
  displayMode: AnnouncementBarDisplayModeSchema.default('marquee'),
  themePreset: AnnouncementBarThemePresetSchema.default('custom'),
  animationSpeedSeconds: z.number().int().min(8).max(40).default(DEFAULT_ANNOUNCEMENT_BAR_ANIMATION_SPEED_SECONDS),
  backgroundColor: HexColorSchema.default('#111827'),
  textColor: HexColorSchema.default('#f9fafb'),
});

export const CountdownBarConfigSchema = z.object({
  text: z.string().trim().min(1, 'Text is required').max(120, 'Text must be 120 characters or fewer'),
  endAt: DatetimeStringSchema,
  linkLabel: z.string().trim().max(32, 'Link label must be 32 characters or fewer').optional().nullable(),
  linkUrl: RelativeOrAbsoluteUrlSchema.optional().nullable(),
  dismissible: z.boolean().default(false),
  themePreset: AnnouncementBarThemePresetSchema.default('brand_blue'),
  expiredBehavior: CountdownBarExpiryBehaviorSchema.default('hide'),
  expiredMessage: z.string().trim().max(120, 'Expired message must be 120 characters or fewer').optional().nullable(),
  density: CountdownBarDensitySchema.default('compact'),
  ctaStyle: CountdownBarCtaStyleSchema.default('subtle'),
  timerStyle: CountdownBarTimerStyleSchema.default('boxed'),
  backgroundColor: HexColorSchema.default('#111827'),
  textColor: HexColorSchema.default('#f9fafb'),
});

export const PromoInfoCardConfigSchema = z.object({
  eyebrow: z.string().trim().max(32, 'Eyebrow must be 32 characters or fewer').optional().nullable(),
  title: z.string().trim().min(1, 'Title is required').max(80, 'Title must be 80 characters or fewer'),
  body: z.string().trim().min(1, 'Body is required').max(240, 'Body must be 240 characters or fewer'),
  linkLabel: z.string().trim().max(32, 'Link label must be 32 characters or fewer').optional().nullable(),
  linkUrl: RelativeOrAbsoluteUrlSchema.optional().nullable(),
  themePreset: PromoInfoCardThemePresetSchema.default('indigo'),
  layoutStyle: PromoInfoCardLayoutStyleSchema.default('feature'),
  density: PromoInfoCardDensitySchema.default('comfortable'),
  ctaStyle: PromoInfoCardCtaStyleSchema.default('outline'),
  icon: PromoInfoCardIconSchema.default('sparkles'),
  iconVisibility: PromoInfoCardIconVisibilitySchema.default('show'),
  backgroundColor: HexColorSchema.default('#111827'),
  textColor: HexColorSchema.default('#f9fafb'),
  accentColor: HexColorSchema.default('#818cf8'),
});

export const RecentPurchasePopupConfigSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(80, 'Title must be 80 characters or fewer').default('Recent purchases'),
  lookbackHours: z.number().int().min(1).max(168).default(24),
  cooldownSeconds: z.number().int().min(5).max(300).default(20),
  maxItems: z.number().int().min(1).max(20).default(8),
  anonymizeMode: z.enum([
    'first_name_city',
    'first_name_country',
    'initial_country',
    'anonymous',
  ]).default('first_name_city'),
  includedProductIds: z.array(z.string().min(1)).max(50).default([]),
});

export const CouponPopupModalConfigSchema = z.object({
  eyebrow: z.string().trim().max(32, 'Eyebrow must be 32 characters or fewer').optional().nullable(),
  title: z.string().trim().min(1, 'Title is required').max(80, 'Title must be 80 characters or fewer'),
  body: z.string().trim().min(1, 'Body is required').max(240, 'Body must be 240 characters or fewer'),
  couponCode: z.string().trim().min(2, 'Coupon code is required').max(40, 'Coupon code must be 40 characters or fewer'),
  primaryButtonLabel: z.string().trim().min(1, 'Primary button label is required').max(24, 'Primary button label must be 24 characters or fewer').default('Copy code'),
  secondaryButtonLabel: z.string().trim().min(1, 'Secondary button label is required').max(24, 'Secondary button label must be 24 characters or fewer').default('Maybe later'),
  disclaimer: z.string().trim().max(100, 'Disclaimer must be 100 characters or fewer').optional().nullable(),
  themePreset: CouponPopupModalThemePresetSchema.default('midnight'),
  trigger: CouponPopupModalTriggerSchema.default('delay'),
  delaySeconds: z.number().int().min(0).max(60).default(6),
  showOncePerSession: z.boolean().default(true),
  reminderHours: z.number().int().min(1).max(720).default(24),
  backgroundColor: HexColorSchema.default('#111827'),
  textColor: HexColorSchema.default('#f8fafc'),
  accentColor: HexColorSchema.default('#7c9cff'),
  heroImageUrl: RelativeOrAbsoluteUrlSchema.optional().nullable(),
  discountDisplay: z.string().trim().max(24, 'Discount display must be 24 characters or fewer').optional().nullable(),
  expiresAt: DatetimeStringSchema.optional().nullable(),
});

export const liveChatContactFieldsValues = [
  'hidden',
  'optional',
] as const;

export const LiveChatContactFieldsSchema = z.enum(liveChatContactFieldsValues);

export const LiveChatConfigSchema = z.object({
  headline: z.string().trim().min(1, 'Headline is required').max(60, 'Headline must be 60 characters or fewer').default('Chat with us'),
  greeting: z.string().trim().min(1, 'Greeting is required').max(240, 'Greeting must be 240 characters or fewer').default('Hi! Send us a message and we will reply as soon as possible.'),
  inputPlaceholder: z.string().trim().min(1, 'Placeholder is required').max(80, 'Placeholder must be 80 characters or fewer').default('Type your message…'),
  contactFields: LiveChatContactFieldsSchema.default('optional'),
  accentColor: HexColorSchema.default('#111827'),
  launcherIconColor: HexColorSchema.default('#f9fafb'),
});

export type LiveChatConfig = z.infer<typeof LiveChatConfigSchema>;

export type AnnouncementBarConfig = z.infer<typeof AnnouncementBarConfigSchema>;
export type CountdownBarConfig = z.infer<typeof CountdownBarConfigSchema>;
export type PromoInfoCardConfig = z.infer<typeof PromoInfoCardConfigSchema>;
export type RecentPurchasePopupConfig = z.infer<typeof RecentPurchasePopupConfigSchema>;
export type CouponPopupModalConfig = z.infer<typeof CouponPopupModalConfigSchema>;

export type StorefrontAddonConfigByType = {
  announcement_bar: AnnouncementBarConfig;
  countdown_bar: CountdownBarConfig;
  promo_info_card: PromoInfoCardConfig;
  recent_purchase_popup: RecentPurchasePopupConfig;
  coupon_popup_modal: CouponPopupModalConfig;
  live_chat: LiveChatConfig;
};

export const StorefrontAddonConfigSchemaByType = {
  announcement_bar: AnnouncementBarConfigSchema,
  countdown_bar: CountdownBarConfigSchema,
  promo_info_card: PromoInfoCardConfigSchema,
  recent_purchase_popup: RecentPurchasePopupConfigSchema,
  coupon_popup_modal: CouponPopupModalConfigSchema,
  live_chat: LiveChatConfigSchema,
} as const satisfies Record<StorefrontAddonType, z.ZodTypeAny>;

const AnnouncementBarDraftSchema = z.object({
  type: z.literal('announcement_bar'),
  slot: StorefrontAddonSlotSchema,
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
  config: AnnouncementBarConfigSchema,
});

const RecentPurchasePopupDraftSchema = z.object({
  type: z.literal('recent_purchase_popup'),
  slot: StorefrontAddonSlotSchema,
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
  config: RecentPurchasePopupConfigSchema,
});

const CountdownBarDraftSchema = z.object({
  type: z.literal('countdown_bar'),
  slot: StorefrontAddonSlotSchema,
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
  config: CountdownBarConfigSchema,
});

const PromoInfoCardDraftSchema = z.object({
  type: z.literal('promo_info_card'),
  slot: StorefrontAddonSlotSchema,
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
  config: PromoInfoCardConfigSchema,
});

const CouponPopupModalDraftSchema = z.object({
  type: z.literal('coupon_popup_modal'),
  slot: StorefrontAddonSlotSchema,
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
  config: CouponPopupModalConfigSchema,
});

const LiveChatDraftSchema = z.object({
  type: z.literal('live_chat'),
  slot: StorefrontAddonSlotSchema,
  enabled: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
  config: LiveChatConfigSchema,
});

export const StorefrontAddonCreateSchema = z.discriminatedUnion('type', [
  AnnouncementBarDraftSchema,
  CountdownBarDraftSchema,
  PromoInfoCardDraftSchema,
  RecentPurchasePopupDraftSchema,
  CouponPopupModalDraftSchema,
  LiveChatDraftSchema,
]);

export const StorefrontAddonUpdateSchema = z.discriminatedUnion('type', [
  AnnouncementBarDraftSchema.extend({
    id: z.string().min(1),
  }),
  CountdownBarDraftSchema.extend({
    id: z.string().min(1),
  }),
  PromoInfoCardDraftSchema.extend({
    id: z.string().min(1),
  }),
  RecentPurchasePopupDraftSchema.extend({
    id: z.string().min(1),
  }),
  CouponPopupModalDraftSchema.extend({
    id: z.string().min(1),
  }),
  LiveChatDraftSchema.extend({
    id: z.string().min(1),
  }),
]);

export type StorefrontAddonCreateInput = z.infer<typeof StorefrontAddonCreateSchema>;
export type StorefrontAddonUpdateInput = z.infer<typeof StorefrontAddonUpdateSchema>;

export interface StorefrontAddonInstanceBase {
  id: string;
  shopId: string;
  type: StorefrontAddonType;
  slot: StorefrontAddonSlot;
  enabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type StorefrontAddonInstance =
  | (StorefrontAddonInstanceBase & {
      type: 'announcement_bar';
      config: AnnouncementBarConfig;
    })
  | (StorefrontAddonInstanceBase & {
      type: 'countdown_bar';
      config: CountdownBarConfig;
    })
  | (StorefrontAddonInstanceBase & {
      type: 'promo_info_card';
      config: PromoInfoCardConfig;
    })
  | (StorefrontAddonInstanceBase & {
      type: 'recent_purchase_popup';
      config: RecentPurchasePopupConfig;
    })
  | (StorefrontAddonInstanceBase & {
      type: 'coupon_popup_modal';
      config: CouponPopupModalConfig;
    })
  | (StorefrontAddonInstanceBase & {
      type: 'live_chat';
      config: LiveChatConfig;
    });

export interface StorefrontAddonCatalogItem {
  type: StorefrontAddonType;
  title: string;
  description: string;
  slots: StorefrontAddonSlot[];
  supportsMultiple: boolean;
  features: string[];
  defaults: StorefrontAddonCreateInput;
}

export interface AnnouncementBarResolvedProps extends AnnouncementBarConfig {
  addonId: string;
}

export interface CountdownBarResolvedProps extends CountdownBarConfig {
  addonId: string;
}

export interface PromoInfoCardResolvedProps extends PromoInfoCardConfig {
  addonId: string;
}

export interface RecentPurchasePopupItem {
  customerLabel: string;
  productTitle: string;
  createdAt: string;
}

export interface RecentPurchasePopupResolvedProps {
  addonId: string;
  title: string;
  cooldownSeconds: number;
  items: RecentPurchasePopupItem[];
}

export interface CouponPopupModalResolvedProps extends CouponPopupModalConfig {
  addonId: string;
}

export interface LiveChatResolvedProps extends LiveChatConfig {
  addonId: string;
}

export type ResolvedStorefrontAddon =
  | {
      id: string;
      type: 'announcement_bar';
      slot: StorefrontAddonSlot;
      component: 'announcement_bar';
      sortOrder: number;
      props: AnnouncementBarResolvedProps;
    }
  | {
      id: string;
      type: 'countdown_bar';
      slot: StorefrontAddonSlot;
      component: 'countdown_bar';
      sortOrder: number;
      props: CountdownBarResolvedProps;
    }
  | {
      id: string;
      type: 'promo_info_card';
      slot: StorefrontAddonSlot;
      component: 'promo_info_card';
      sortOrder: number;
      props: PromoInfoCardResolvedProps;
    }
  | {
      id: string;
      type: 'recent_purchase_popup';
      slot: StorefrontAddonSlot;
      component: 'recent_purchase_popup';
      sortOrder: number;
      props: RecentPurchasePopupResolvedProps;
    }
  | {
      id: string;
      type: 'coupon_popup_modal';
      slot: StorefrontAddonSlot;
      component: 'coupon_popup_modal';
      sortOrder: number;
      props: CouponPopupModalResolvedProps;
    }
  | {
      id: string;
      type: 'live_chat';
      slot: StorefrontAddonSlot;
      component: 'live_chat';
      sortOrder: number;
      props: LiveChatResolvedProps;
    };

export interface StorefrontAddonBootstrap {
  items: ResolvedStorefrontAddon[];
}
